import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Sprout, Medal, Trophy, LayoutGrid, QrCode } from "lucide-react";
import Reveal from "@/components/Reveal";
import { API } from "@/lib/api";

const PIX_KEY = "projetobonsaidoacao@gmail.com";

const TIERS = [
  { icon: Sprout, value: 30, label: "1 mês de aulas", text: "Garante a estrutura de treino de uma criança por um mês." },
  { icon: Medal, value: 60, label: "1 faixa nova", text: "Cada graduação merece ser celebrada com dignidade." },
  { icon: LayoutGrid, value: 90, label: "1 placa de tatame", text: "Amplia a área de treino seguro para toda a turma." },
  { icon: Trophy, value: 200, label: "1 kimono infantil", text: "Um kimono novo para um aluno que não pode comprar." },
];

export default function Donations() {
  const [selected, setSelected] = useState(60);

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(PIX_KEY);
      toast.success("Chave Pix copiada! Obrigado por apoiar o Bonsai.");
    } catch {
      toast.error(`Copie manualmente: ${PIX_KEY}`);
    }
  };

  return (
    <section id="doacoes" className="relative py-28 sm:py-36 bg-surface/30 border-y border-white/5 overflow-hidden" data-testid="donations-section">
      <div aria-hidden="true" className="absolute left-[-10%] top-[20%] w-[40vw] h-[40vw] max-w-[560px] max-h-[560px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(220,38,38,0.4) 0%, transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] text-gold mb-4" data-testid="donations-eyebrow">APOIE O PROJETO</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl" data-testid="donations-title">
            Sua doação rega <span className="text-gold">o futuro de uma criança.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl leading-relaxed">
            O Bonsai é 100% gratuito e aberto ao público — inclusive para crianças de casas de abrigo — e sobrevive de doações. Qualquer valor faz diferença — via Pix, direto e sem intermediários.
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t, i) => (
            <Reveal key={t.value} delay={i * 0.12}>
              <button
                onClick={() => setSelected(t.value)}
                data-testid={`donation-tier-${t.value}`}
                className={`w-full text-left h-full rounded-2xl border p-7 transition-all duration-400 ${
                  selected === t.value
                    ? "border-gold bg-gold/10 shadow-[0_0_36px_rgba(212,175,55,0.15)]"
                    : "border-white/10 bg-surface hover:border-gold/40"
                }`}
              >
                <t.icon size={26} className={selected === t.value ? "text-gold" : "text-zinc-500"} />
                <p className="font-mono text-3xl font-bold mt-5 text-gold">R$ {t.value}</p>
                <p className="font-display font-semibold mt-1">{t.label}</p>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{t.text}</p>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.25}>
          <div className="mt-10 bg-obsidian border border-gold/25 rounded-2xl p-8 sm:p-10 flex flex-col md:flex-row md:items-center gap-8 justify-between" data-testid="pix-card">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] text-zinc-500">CHAVE PIX (E-MAIL)</p>
              <p className="font-mono text-xl sm:text-2xl text-gold mt-2 break-all" data-testid="pix-key">{PIX_KEY}</p>
              <p className="text-sm text-zinc-500 mt-3">
                Valor sugerido: <span className="text-zinc-300 font-semibold">R$ {selected}</span> — ou qualquer valor, de coração.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6 shrink-0">
              <div className="text-center">
                <div className="bg-white rounded-2xl p-3 inline-block" data-testid="pix-qr">
                  <img src={`${API}/pix-qr`} alt="QR Code Pix do Projeto Bonsai BJJ" className="w-36 h-36" />
                </div>
                <p className="font-mono text-[10px] tracking-widest text-zinc-500 mt-2 flex items-center justify-center gap-1.5">
                  <QrCode size={12} className="text-gold" /> APONTE A CÂMERA
                </p>
              </div>
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={copyPix}
                data-testid="copy-pix-key-button"
                className="shrink-0 inline-flex items-center gap-3 font-display font-semibold bg-gold text-obsidian px-8 py-4 rounded-full hover:bg-gold-amber hover:shadow-[0_0_32px_rgba(212,175,55,0.45)] transition-all duration-300"
              >
                <Copy size={18} /> Copiar Chave Pix
              </motion.button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
